import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Calendar as CalIcon, Users } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import BrandCalendar from "@/components/BrandCalendar";
import DateRangePicker from "@/components/DateRangePicker";
import { Drawer, DrawerContent, DrawerTrigger, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import BrandButton from "@/components/brand/BrandButton";
import BrandInput, { BrandField } from "@/components/brand/BrandInput";
import { useTheme, currentBrandSlug } from "@/contexts/ThemeProvider";
import { cn } from "@/lib/utils";

const fmt = (d) => (d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

// Local-time YYYY-MM-DD formatter. NEVER use `toISOString().slice(0, 10)` for a
// user-clicked calendar date — it converts to UTC and can shift the calendar
// day forward or backward depending on the user's timezone offset, producing
// an off-by-one selection bug.
const toLocalISO = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const SearchBar = ({ defaultBrand, scope = "unified", onSubmitNav }) => {
  const { theme } = useTheme();
  const slug = currentBrandSlug(theme);
  const navigate = useNavigate();
  const [destination, setDestination] = useState("");
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [guests, setGuests] = useState(2);
  const [mobileOpen, setMobileOpen] = useState(false);

  const submit = (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    if (destination) params.set("q", destination);
    if (range?.from) params.set("from", toLocalISO(range.from));
    if (range?.to) params.set("to", toLocalISO(range.to));
    if (guests) params.set("guests", String(guests));
    if (defaultBrand) params.set("brand", defaultBrand);
    const dest =
      scope === "castlewood"
        ? `/castlewood/search?${params.toString()}`
        : scope === "vela"
        ? `/vela/search?${params.toString()}`
        : `/search?${params.toString()}`;
    onSubmitNav?.();
    setMobileOpen(false);
    navigate(dest);
  };

  const ctaLabel =
    slug === "vela" ? "Find Your Stay" : slug === "castlewood" ? "Check Availability" : "Find a Stay";

  return (
    <>
      {/* Desktop layout */}
      <form
        onSubmit={submit}
        className={cn(
          "hidden md:grid w-full grid-cols-[1.4fr_1.6fr_1fr_auto] items-end gap-3 p-4 bg-[hsl(var(--background))] border border-border shadow-[var(--shadow-elev-1)]",
          slug === "castlewood" && "rounded-[2px] border-foreground/30",
          slug === "brionj" && "rounded-none border-[hsl(var(--brand-accent))]/30 bg-[hsl(var(--card))]",
          slug === "vela" && "rounded-[12px]",
          slug === "gmh" && "rounded-[10px]",
          slug === "admin" && "rounded-[8px]"
        )}
        data-testid="searchbar-desktop"
      >
        <BrandField label="Destination" htmlFor="sb-dest">
          <BrandInput id="sb-dest" data-testid="searchbar-destination" placeholder="Anywhere" value={destination} onChange={(e) => setDestination(e.target.value)} />
        </BrandField>
        <BrandField label="Dates" htmlFor="sb-dates">
          <Popover>
            <PopoverTrigger asChild>
              <button id="sb-dates" type="button" data-testid="searchbar-dates" className={cn(
                "w-full h-11 px-3 border border-border text-left text-[14px] font-ui bg-background text-foreground flex items-center gap-2",
                slug === "castlewood" ? "rounded-[2px] border-foreground/40" : "",
                slug === "brionj" ? "rounded-none" : "",
                slug === "vela" ? "rounded-[10px]" : "",
                slug === "gmh" ? "rounded-[8px]" : ""
              )}>
                <CalIcon size={14} className="text-muted-foreground" />
                {range?.from ? `${fmt(range.from)}${range.to ? ` — ${fmt(range.to)}` : ""}` : <span className="text-muted-foreground">Arrive — Depart</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 border-0 bg-transparent shadow-none" align="start">
              <BrandCalendar
                mode="range"
                numberOfMonths={2}
                selected={range}
                onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
                onClear={() => setRange({ from: undefined, to: undefined })}
                onToday={() => {
                  const t = new Date(); t.setHours(0, 0, 0, 0);
                  setRange({ from: t, to: undefined });
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </BrandField>
        <BrandField label="Guests" htmlFor="sb-guests">
          <BrandInput id="sb-guests" data-testid="searchbar-guests" type="number" min={1} max={20} value={guests} onChange={(e) => setGuests(Number(e.target.value || 1))} />
        </BrandField>
        <div>
          <BrandButton type="submit" className="h-11 px-6" data-testid="searchbar-submit">
            <Search size={14} className="mr-2" /> {ctaLabel}
          </BrandButton>
        </div>
      </form>

      {/* Mobile collapsed */}
      <Drawer open={mobileOpen} onOpenChange={setMobileOpen}>
        <DrawerTrigger asChild>
          <button type="button" data-testid="searchbar-mobile-trigger" className={cn(
            "md:hidden w-full flex items-center justify-between gap-3 p-4 bg-card border border-border text-left text-[13px]",
            slug === "castlewood" ? "rounded-[2px]" : "",
            slug === "brionj" ? "rounded-none" : "",
            slug === "vela" ? "rounded-[10px]" : "",
            slug === "gmh" ? "rounded-[10px]" : ""
          )}>
            <span className="flex items-center gap-2"><MapPin size={14} /> {destination || "Anywhere"}</span>
            <span className="flex items-center gap-2"><CalIcon size={14} /> {range?.from ? fmt(range.from) : "Dates"}</span>
            <span className="flex items-center gap-2"><Users size={14} /> {guests}</span>
          </button>
        </DrawerTrigger>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-display">Find a stay</DrawerTitle>
          </DrawerHeader>
          <form onSubmit={submit} className="p-4 grid gap-4">
            <BrandField label="Destination"><BrandInput data-testid="searchbar-mobile-destination" placeholder="Anywhere" value={destination} onChange={(e) => setDestination(e.target.value)} /></BrandField>
            <BrandField label="Dates">
              <DateRangePicker
                mode="range"
                value={{
                  from: range?.from ? toLocalISO(range.from) : "",
                  to:   range?.to   ? toLocalISO(range.to)   : "",
                }}
                onChange={(v) => setRange({
                  from: v.from ? new Date(v.from + "T00:00:00") : undefined,
                  to:   v.to   ? new Date(v.to   + "T00:00:00") : undefined,
                })}
                placeholder="Arrive — Depart"
                testId="searchbar-mobile-dates"
                numberOfMonths={1}
                align="center"
              />
            </BrandField>
            <BrandField label="Guests"><BrandInput type="number" min={1} max={20} data-testid="searchbar-mobile-guests" value={guests} onChange={(e) => setGuests(Number(e.target.value || 1))} /></BrandField>
          </form>
          <DrawerFooter>
            <BrandButton onClick={submit} data-testid="searchbar-mobile-submit">{ctaLabel}</BrandButton>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default SearchBar;
